import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";

/** Gap between the trigger and the panel, and the panel's minimum inset from a viewport edge. */
const GAP = 8;

/** Below this much room underneath the trigger, the panel opens upwards instead. */
const FLIP_THRESHOLD = 240;

/**
 * A dropdown panel anchored to a trigger but rendered on `document.body`.
 *
 * The Role and Offices managers open from inside a table cell, and that cell is
 * overflow-hidden inside a horizontally scrolling table: a panel positioned within it
 * is cut off at the cell's edge, which is what these two were. A portal takes the
 * panel out of every clipping ancestor, and fixed coordinates read off the trigger put
 * it back where it looks anchored — clamped to the viewport, and flipped above the
 * trigger when there isn't room below, so it can't open off-screen either.
 *
 * Being outside the trigger's subtree, the panel also can't rely on a
 * `contains(e.target)` check on one ref, so closing is owned here: a mousedown outside
 * *both* nodes closes, and so does Escape.
 */
const AnchoredPanel = ({ anchorRef, width, className = "", onRequestClose, children }) => {
  const [panelEl, setPanelEl] = useState(null);
  const [style, setStyle] = useState(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    // Right-aligned to the trigger, as it read before, then pulled back inside
    // whichever viewport edge that alignment would have crossed.
    const left = Math.min(Math.max(rect.right - width, GAP), Math.max(GAP, vw - width - GAP));

    const roomBelow = vh - rect.bottom - GAP * 2;
    const roomAbove = rect.top - GAP * 2;
    const openUp = roomBelow < FLIP_THRESHOLD && roomAbove > roomBelow;
    const maxHeight = Math.max(160, openUp ? roomAbove : roomBelow);

    setStyle({
      position: "fixed",
      left,
      width,
      maxHeight,
      ...(openUp ? { bottom: vh - rect.top + GAP } : { top: rect.bottom + GAP }),
    });
  }, [anchorRef, width]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    // Anything that moves the trigger moves the panel with it. Scroll is watched in the
    // capture phase because the table scrolls in its own container, and those events
    // don't bubble to window.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [place]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (panelEl?.contains(e.target)) return;
      // The trigger toggles itself; closing here too would reopen-then-close it.
      if (anchorRef.current?.contains(e.target)) return;
      onRequestClose?.();
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onRequestClose?.();
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [panelEl, anchorRef, onRequestClose]);

  return ReactDOM.createPortal(
    <div
      ref={setPanelEl}
      // Hidden until it has been measured, so it never paints at the top-left corner
      // on the way to its real position.
      style={style ?? { position: "fixed", top: 0, left: 0, visibility: "hidden" }}
      className={`z-[9999] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 ${className}`}
    >
      {children}
    </div>,
    document.body
  );
};

export default AnchoredPanel;
